import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import "./HeroVideo.css";

const POSTER_DESKTOP = "/poster/desktop-poster-high-7.jpg";
const POSTER_MOBILE = "/poster/mobile-poster-high-6.jpg";

function usePortraitAspect() {
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-aspect-ratio: 1/1)");
    const update = () => setIsPortrait(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isPortrait;
}

/** `auto` so mobile Safari / iOS WebKit buffer enough for muted autoplay. */
function useHeroPreload() {
  return "auto";
}

function buildVideoSources(isPortrait) {
  const base = isPortrait
    ? "mobile-front-1k-540"
    : "desktop-front-6k-1080-v3-mute";
  const webm = { src: `/media/${base}.webm`, type: "video/webm" };
  const mp4 = { src: `/media/${base}.mp4`, type: "video/mp4" };
  // Phone Safari: MP4 (H.264) first — no WebM decode; avoids wasted probe on WebM.
  // Desktop: WebM first for Firefox/Linux without licensed H.264 MP4.
  return isPortrait ? [mp4, webm] : [webm, mp4];
}

/**
 * Normal play() + loop — avoids stepping currentTime every frame, which forces
 * constant decoder seeks and is very janky in Firefox (especially WebM/VP9).
 */
function useNativeHeroPlayback(videoRef, active, layoutKey) {
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !active) {
      return undefined;
    }

    el.muted = true;
    el.defaultMuted = true;
    el.volume = 0;

    const tryPlay = () => {
      const p = el.play();
      if (p !== undefined && typeof p.catch === "function") {
        p.catch(() => {});
      }
    };

    const onCanPlay = () => {
      tryPlay();
    };

    const onLoadedData = () => {
      tryPlay();
    };

    if (el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      tryPlay();
    } else {
      el.addEventListener("canplay", onCanPlay, { once: true });
      el.addEventListener("loadeddata", onLoadedData, { once: true });
    }

    return () => {
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("loadeddata", onLoadedData);
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    };
  }, [videoRef, active, layoutKey]);
}

function HeroVideo({
  prefersReducedMotion,
  stripeCombinationReady = true,
  playbackActive = true,
}) {
  const isPortrait = usePortraitAspect();
  const preload = useHeroPreload();
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const videoRef = useRef(null);
  const hasRevealedRef = useRef(false);
  const wasPlaybackActiveRef = useRef(playbackActive);

  const sourceCandidates = useMemo(
    () => buildVideoSources(isPortrait),
    [isPortrait],
  );

  useEffect(() => {
    setIsVideoVisible(false);
    hasRevealedRef.current = false;
  }, [isPortrait]);

  const videoKey = isPortrait ? "m" : "d";

  useEffect(() => {
    const el = videoRef.current;
    if (!el || prefersReducedMotion) {
      return;
    }
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
  }, [videoKey, prefersReducedMotion]);

  /** Do not gate on scanline stripe — if that event is late, play() never ran. Stripe only affects hero visibility CSS. */
  const playbackAllowed = !prefersReducedMotion && playbackActive;

  useNativeHeroPlayback(videoRef, playbackAllowed, videoKey);

  /**
   * iOS WebKit pauses inline video when the app backgrounds; returning does not
   * always restart playback. `play()` in the same tick as `visibilitychange` often
   * fails — defer with rAF. Also listen in capture phase and on first pointer
   * after resume (visibility events are occasionally missing on iOS).
   */
  useEffect(() => {
    if (typeof document === "undefined" || !playbackAllowed) {
      return undefined;
    }

    const tryPlayElement = (el) => {
      el.muted = true;
      el.volume = 0;
      const attempt = () => {
        const p = el.play();
        if (p !== undefined && typeof p.catch === "function") {
          p.catch(() => {
            try {
              const t = el.currentTime;
              el.currentTime = t;
            } catch {
              /* ignore */
            }
            const p2 = el.play();
            if (p2 !== undefined && typeof p2.catch === "function") {
              p2.catch(() => {});
            }
          });
        }
      };
      attempt();
    };

    const scheduleResumeIfVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const el = videoRef.current;
      if (!el) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (document.visibilityState !== "visible") {
            return;
          }
          const v = videoRef.current;
          if (v) {
            tryPlayElement(v);
          }
        });
      });
    };

    const onPageShow = (e) => {
      if (e.persisted) {
        scheduleResumeIfVisible();
      }
      scheduleResumeIfVisible();
    };

    const onFocusCapture = () => {
      scheduleResumeIfVisible();
    };

    let lastPointerResume = 0;
    const onPointerAfterPause = () => {
      const el = videoRef.current;
      if (!el || !el.paused) {
        return;
      }
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastPointerResume < 400) {
        return;
      }
      lastPointerResume = now;
      scheduleResumeIfVisible();
    };

    document.addEventListener("visibilitychange", scheduleResumeIfVisible);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocusCapture, true);
    document.addEventListener("focus", onFocusCapture, true);
    document.addEventListener("resume", scheduleResumeIfVisible);
    window.addEventListener("pointerdown", onPointerAfterPause, {
      capture: true,
      passive: true,
    });
    window.addEventListener("touchstart", onPointerAfterPause, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener("visibilitychange", scheduleResumeIfVisible);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocusCapture, true);
      document.removeEventListener("focus", onFocusCapture, true);
      document.removeEventListener("resume", scheduleResumeIfVisible);
      window.removeEventListener("pointerdown", onPointerAfterPause, true);
      window.removeEventListener("touchstart", onPointerAfterPause, true);
    };
  }, [playbackAllowed, videoKey]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || prefersReducedMotion) {
      return;
    }
    if (!playbackActive) {
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    }
  }, [playbackActive, prefersReducedMotion]);

  /** SPA return to home: play() right after route change often fails if hero was off-route. */
  useEffect(() => {
    const wasActive = wasPlaybackActiveRef.current;
    wasPlaybackActiveRef.current = playbackActive;

    if (prefersReducedMotion || !playbackActive || wasActive) {
      return undefined;
    }

    const el = videoRef.current;
    if (!el) {
      return undefined;
    }

    if (hasRevealedRef.current) {
      setIsVideoVisible(true);
    }

    const tryResume = () => {
      el.muted = true;
      el.defaultMuted = true;
      el.volume = 0;
      const p = el.play();
      if (p !== undefined && typeof p.catch === "function") {
        p.catch(() => {});
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(tryResume);
    });
    const fallbackId = window.setTimeout(tryResume, 150);

    return () => {
      window.clearTimeout(fallbackId);
    };
  }, [playbackActive, prefersReducedMotion, videoKey]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || prefersReducedMotion) {
      return;
    }

    let cancelled = false;
    const enough = HTMLMediaElement.HAVE_CURRENT_DATA;

    function finishReveal() {
      if (cancelled || hasRevealedRef.current) {
        return;
      }
      hasRevealedRef.current = true;

      const applyVisible = () => {
        if (!cancelled) {
          setIsVideoVisible(true);
          requestAnimationFrame(() => {
            if (cancelled) {
              return;
            }
            el.muted = true;
            el.volume = 0;
            const p = el.play();
            if (p !== undefined && typeof p.catch === "function") {
              p.catch(() => {});
            }
          });
        }
      };

      if (typeof el.requestVideoFrameCallback === "function") {
        el.requestVideoFrameCallback(() => {
          if (cancelled) {
            return;
          }
          requestAnimationFrame(applyVisible);
        });
      } else {
        requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }
          requestAnimationFrame(applyVisible);
        });
      }
    }

    function onCanPlayThrough() {
      tryRevealIfEnoughData();
    }

    function onCanPlay() {
      tryRevealIfEnoughData();
    }

    function tryRevealIfEnoughData() {
      if (cancelled || hasRevealedRef.current) {
        return;
      }
      if (el.readyState >= enough) {
        el.removeEventListener("canplay", onCanPlay);
        el.removeEventListener("canplaythrough", onCanPlayThrough);
        finishReveal();
      }
    }

    el.addEventListener("canplaythrough", onCanPlayThrough, { once: true });
    el.addEventListener("canplay", onCanPlay);
    tryRevealIfEnoughData();

    const safetyMs = 12000;
    const timeoutId = window.setTimeout(() => {
      if (!cancelled && !hasRevealedRef.current) {
        finishReveal();
      }
    }, safetyMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("canplaythrough", onCanPlayThrough);
    };
  }, [videoKey, prefersReducedMotion, playbackActive]);

  const posterPicture = (
    <picture className="hero-poster-picture">
      <source
        media="(max-aspect-ratio: 1/1)"
        srcSet={POSTER_MOBILE}
      />
      <img
        className="hero-poster-base"
        src={POSTER_DESKTOP}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
      />
    </picture>
  );

  if (prefersReducedMotion) {
    return (
      <div
        className={clsx(
          "hero-media",
          "hero-media--poster-fallback",
          !stripeCombinationReady && "hero-media--awaiting-stripe",
        )}
      >
        {posterPicture}
      </div>
    );
  }

  return (
    <div
      className={clsx("hero-media", {
        "hero-media--awaiting-stripe": !stripeCombinationReady,
      })}
    >
      {posterPicture}
      <div
        className={clsx("hero-media__video-wrap", {
          "is-visible": isVideoVisible,
        })}
      >
        <video
          ref={videoRef}
          key={videoKey}
          className="hero-video"
          muted
          autoPlay
          loop
          playsInline
          preload={preload}
          aria-hidden="true"
          tabIndex={-1}
        >
          {sourceCandidates.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
      </div>
    </div>
  );
}

export default HeroVideo;
